import React, { useEffect, useState } from "react";
import { Step, StepLabel, Stepper, Typography } from "@mui/material";
import { AuthPageWrapper } from "../../components/layout/AuthPageWrapper";
import { useHistory } from "react-router";
import { Routes } from "../../root/routes";
import { MultiFactorAuthSetup } from "../../components/organisms/auth/MultiFactorAuthSetup";
import { MultiFactorAuthSetupComplete } from "../../components/organisms/auth/MultiFactorAuthSetupComplete";
import { NetworkRequest, NetworkRequestState } from "../../../common/models";
import { Callout, CalloutVariant } from "../../components/Callout";
import { Error } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { parsePhoneNumber } from "libphonenumber-js";
import { useLocalStorage } from "../../hooks/useLocalStorage";

const useStyles = makeStyles({
  callout: {
    marginLeft: "43px",
  },
});

export const MultiFactorAuthSetupPage = ({
  token,
  jwt,
  validateMfaCode,
  validateMfaCodeRequest,
  validateMfaCodeLoading,
  wrongCodeError,
  createMfaChallenge,
  createMfaChallengeRequest,
  createMfaChallengeError,
  maxAttemptsError,
}: {
  token: string;
  jwt: any;
  validateMfaCode: (opts: { code: string; phone: string }) => Promise<void>;
  validateMfaCodeRequest: NetworkRequestState;
  validateMfaCodeLoading: boolean;
  wrongCodeError: boolean;
  createMfaChallenge: (opts: { phone: string; token: string }) => Promise<void>;
  createMfaChallengeRequest: NetworkRequestState;
  createMfaChallengeError: boolean;
  maxAttemptsError: boolean;
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [phone, setPhone] = useState("");
  const history = useHistory();
  const [_, setIsNewMfa] = useLocalStorage("polarisNewMFA", "false");
  const backAction = () => {
    if (activeStep === 0) {
      history.push(Routes.auth.login);
    } else {
      setActiveStep(0);
    }
  };

  useEffect(() => {
    if (jwt) {
      if (jwt.phone) {
        setPhone(jwt.phone);
      }
      if (jwt.verificationSid) {
        setActiveStep(1);
      }
    }
  }, [jwt]);

  useEffect(() => {
    if (jwt && activeStep == 0) {
      setIsNewMfa("true");
    }
  }, [jwt, activeStep]);

  const backActionLabel = activeStep === 0 ? "Back to login" : "Back";
  const activeStepIndex =
    NetworkRequest.isFinished(validateMfaCodeRequest) &&
    validateMfaCodeRequest.success &&
    !wrongCodeError
      ? 2
      : activeStep;

  return (
    <AuthPageWrapper
      navLinkBack={backAction}
      navLinkBackLabel={backActionLabel}
      panelWidthStyle="600px"
    >
      <div className="fill align-center">
        <img src="/assets/images/nav-icon-truevault.png" width={"40px"} />
      </div>
      <Typography color="primary" fontWeight="bold" className="text-center fill my-sm text-t5">
        Secure your account with two-factor authentication
      </Typography>

      <Stepper
        activeStep={activeStepIndex}
        alternativeLabel
        className="stepper pt-std mb-std w-100"
      >
        <Step key="enter-number" active={activeStep === 0}>
          <StepLabel>Enter Mobile Number</StepLabel>
        </Step>
        <Step key="verify-code" active={activeStep === 1}>
          <StepLabel>Verify Code</StepLabel>
        </Step>
      </Stepper>

      {createMfaChallengeError && (
        <Callout
          icon={Error}
          variant={CalloutVariant.Secondary}
          title="System currently unable to send a code for authentication"
          className="mb-std w-100"
        >
          <p className={classes.callout}>
            Please{" "}
            <a
              href="#"
              onClick={() => {
                (window as any).Beacon("open");
              }}
            >
              contact support
            </a>{" "}
            for assistance.
          </p>
        </Callout>
      )}

      {maxAttemptsError && (
        <Callout
          icon={Error}
          variant={CalloutVariant.Secondary}
          title="Max validation attempts reached, please wait 10 minutes and try again."
          className="mb-std w-100"
        />
      )}

      {!createMfaChallengeError && !maxAttemptsError && (
        <div className="text-center fill">
          <p className="mt-0">
            For added security,&nbsp;
            {jwt.organizationName ? (
              <Typography display="inline" fontWeight="bold" color="primary">
                {jwt.organizationName}
              </Typography>
            ) : (
              "your organization"
            )}
            &nbsp;requires two-factor authentication for access to your account.
          </p>
        </div>
      )}

      {activeStep === 0 ? (
        <MultiFactorAuthSetup
          setPhone={setPhone}
          createMfaChallenge={async (phone) => {
            await createMfaChallenge({ phone, token });
            setActiveStep(1);
          }}
          isLoading={
            createMfaChallengeRequest && NetworkRequest.anyRunning(createMfaChallengeRequest)
          }
        />
      ) : (
        <MultiFactorAuthSetupComplete
          phone={phone}
          wrongCodeError={wrongCodeError}
          resendCode={() => createMfaChallenge({ phone, token })}
          validateMfaCode={(code) =>
            validateMfaCode({
              code,
              phone: parsePhoneNumber(phone, "US")?.number,
            })
          }
          isLoading={validateMfaCodeLoading}
        />
      )}
    </AuthPageWrapper>
  );
};
